import {
  Button,
  CheckIcon,
  Chip,
  CloseIcon,
  HistoryIcon,
  Table,
  type TableColumns,
} from "@bp/ui-components";
import { useMemo, useRef, useState } from "react";
import { useQuery } from "urql";
import { getFragmentData, graphql } from "../../client/gql";
import { useAuthClaims } from "../../hooks/useAuthClaims";
import { Avatar } from "../Avatar/Avatar";
import {
  type ActivityItem,
  type NameMap,
  auditActionLabel,
  groupByDay,
  parseMetadata,
  toActivityItems,
} from "./activity";
import styles from "./AuditActivityList.module.scss";

// Colocated fragments — each query fetches only what the list renders.
const AuditActivityList_AuditEvent = graphql(`
  fragment AuditActivityList_AuditEvent on AuditEvent {
    id
    action
    occurredAt
    actorType
    actorId
    targetType
    targetId
    metadata
    sessionUid
    loginCid
  }
`);

const AuditActivityList_User = graphql(`
  fragment AuditActivityList_User on User {
    uuid
    email
    profiles {
      uuid
      listName
    }
  }
`);

const AuditActivityList_Org = graphql(`
  fragment AuditActivityList_Org on Organization {
    uuid
    name
  }
`);

const AuditActivityListQuery = graphql(`
  query AuditActivityList_Query($userUuid: ID, $days: Int, $limit: Int) {
    auditEvents(userUuid: $userUuid, days: $days, limit: $limit) {
      ...AuditActivityList_AuditEvent
    }
  }
`);

const AuditActivityListUsersQuery = graphql(`
  query AuditActivityList_UsersQuery {
    users {
      ...AuditActivityList_User
    }
  }
`);

const AuditActivityListOrgsQuery = graphql(`
  query AuditActivityList_OrgsQuery {
    organizations {
      ...AuditActivityList_Org
    }
  }
`);

export { auditActionLabel } from "./activity";

// Categories mirror the design's `cat` facet (user / access / client / federation).
type Category = "user" | "access" | "client" | "federation";

const CATEGORY_OF: Record<Category, RegExp> = {
  access:
    /^(LOGIN|LOGOUT|PROFILE_SWITCHED|MFA|PASSKEY_LOGIN|SESSION|ACCESS_GRANTED|ACCOUNT_ACTIVATED|RECOVERY_CODE_(USED|FAILED))/,
  federation: /^FEDERATION_/,
  client: /^(CLIENT_|BACKCHANNEL_)/,
  user: /./,
};

function categoryOf(action: string): Category {
  return (
    (Object.keys(CATEGORY_OF) as Category[]).find((c) => CATEGORY_OF[c].test(action)) ?? "user"
  );
}

const CATEGORIES: { id: "all" | Category; label: string }[] = [
  { id: "all", label: "Alle" },
  { id: "user", label: "Benutzer" },
  { id: "access", label: "Zugriff" },
  { id: "client", label: "Clients" },
  { id: "federation", label: "Föderation" },
];

const DAYS = 90;
const PAGE = 20;

/** An event is "unattributed" when it comes from the shared omni-admin / a
 *  non-user actor (system) — the design flags these with a warn badge. */
function isUnattributed(actorType?: string | null): boolean {
  if (!actorType) return true;
  return !/^user$/i.test(actorType);
}

function actorLabel(
  names: NameMap,
  actorType?: string | null,
  actorId?: string | null,
  metadata?: string | null,
): string {
  const m = parseMetadata(metadata);
  const embedded = typeof m.actorName === "string" && m.actorName ? m.actorName : undefined;
  const resolved = (actorId ? names.get(actorId) : undefined) ?? embedded;
  if (isUnattributed(actorType)) return resolved || actorId || actorType || "geteilter Zugang";
  return resolved || actorId || "Benutzer";
}

/** Resolve the target: prefer the id→name map (user / profile / organization),
 *  then a name embedded in the metadata JSON, then the raw id, then "—". */
function targetLabel(names: NameMap, targetId?: string | null, metadata?: string | null): string {
  const resolved = targetId ? names.get(targetId) : undefined;
  if (resolved) return resolved;
  const m = parseMetadata(metadata);
  const candidate =
    m.targetName ?? m.targetEmail ?? m.target ?? m.clientName ?? m.clientId ?? m.email ?? m.name;
  if (typeof candidate === "string" && candidate) return candidate;
  return targetId || "—";
}

type Props = {
  /** Restrict to one user; omit for the global feed. */
  userUuid?: string;
  /**
   * The account's profile uuids: support actions are recorded under the acting
   * profile, so only they tell the person's own actions from received ones.
   */
  profileUuids?: readonly string[];
  /** Per-user default is the compact day-grouped list; the audit page asks for the table. */
  variant?: "compact" | "table";
  /** Initial window; "Mehr laden" grows it. */
  limit?: number;
  /** Text when there are no events. */
  emptyNote?: string;
};

/** One line of a person's activity: status, what, detail, device — as the auth-server shows it. */
function ActivityRow({ item }: { item: ActivityItem }) {
  const Icon = item.ok ? CheckIcon : CloseIcon;
  const meta = [item.device, item.ip, item.note].filter(Boolean).join(" · ");
  return (
    <div className={styles.row} data-status={item.ok ? "ok" : "failed"}>
      <span className={`${styles.ic} ${item.ok ? styles.icOk : styles.icErr}`}>
        <Icon style={{ width: 14, height: 14, color: "currentColor" }} />
      </span>
      <div className={styles.main}>
        <div className={styles.labelLine}>
          <span className={styles.label}>{item.label}</span>
          {item.newDevice && (
            <Chip
              value="Neues Gerät"
              isSmall
              bgColor="var(--color-warning-lightest)"
              color="var(--color-warning)"
            />
          )}
        </div>
        {item.detail && <div className={styles.detail}>{item.detail}</div>}
        {meta && <div className={styles.meta}>{meta}</div>}
      </div>
      <span className={styles.when}>{item.time}</span>
    </div>
  );
}

/**
 * Audit activity from pim-auth-server and pim-graphql-server, read the way the
 * auth-server's Kontoaktivität reads it (see `activity.ts`). The `auditEvents`
 * query is omni-admin only, so it is paused for everyone else.
 *
 * Per person: grouped by day, newest first, with "Mehr laden". The global feed
 * (or `variant="table"`) renders the audit table — Wann / Akteur / Aktion /
 * Ziel / Gerät — with category filter chips.
 */
export function AuditActivityList({
  userUuid,
  profileUuids,
  variant,
  limit: initialLimit = PAGE,
  emptyNote,
}: Props) {
  const { pimAuthClaims } = useAuthClaims();
  const [cat, setCat] = useState<"all" | Category>("all");
  const [limit, setLimit] = useState(initialLimit);
  // Only omni-admins carry an omni-organization claim; the auditEvents query is
  // omni-only server-side, so we pause it for everyone else (and the backend
  // stays the real gate — a Forbidden surfaces via the error branch below).
  const isOmniAdmin = useMemo(() => {
    try {
      return Boolean(pimAuthClaims.getOmniOrganization());
    } catch {
      return false;
    }
  }, [pimAuthClaims]);
  const [{ data, fetching, error }] = useQuery({
    query: AuditActivityListQuery,
    variables: { userUuid: userUuid ?? null, days: DAYS, limit },
    pause: !isOmniAdmin,
  });

  // Resolve actor/target UUIDs → display names. Only the table shows the
  // Akteur/Ziel columns, so pause these (large) list queries for the per-user
  // variant. urql caches, so other pages usually prime them.
  const isFeed = variant === "table" || !userUuid;
  const pauseLists = !isOmniAdmin || !isFeed;
  const [{ data: usersData }] = useQuery({
    query: AuditActivityListUsersQuery,
    pause: pauseLists,
  });
  const [{ data: orgsData }] = useQuery({
    query: AuditActivityListOrgsQuery,
    pause: pauseLists,
  });
  // Keep every name ever seen: the (large) list results can drop to undefined
  // for a render while urql refetches, and the table must not fall back to
  // raw uuids in between.
  const namesRef = useRef<NameMap>(new Map());
  const names = useMemo(() => {
    const map = new Map(namesRef.current);
    for (const u of getFragmentData(AuditActivityList_User, usersData?.users ?? [])) {
      const label = u.profiles?.[0]?.listName || u.email || u.uuid;
      if (label) map.set(u.uuid, label);
      for (const p of u.profiles ?? []) {
        if (p.uuid && p.listName) map.set(p.uuid, p.listName);
      }
    }
    for (const o of getFragmentData(AuditActivityList_Org, orgsData?.organizations ?? [])) {
      if (o.uuid && o.name) map.set(o.uuid, o.name);
    }
    namesRef.current = map;
    return map;
  }, [usersData, orgsData]);

  const allEvents = getFragmentData(AuditActivityList_AuditEvent, data?.auditEvents ?? []);
  const items = useMemo(
    () =>
      toActivityItems(allEvents, {
        names,
        subjectUserUuid: isFeed ? undefined : userUuid,
        subjectProfileUuids: isFeed ? undefined : profileUuids,
      }),
    [allEvents, names, isFeed, userUuid, profileUuids],
  );

  const noteClass = isFeed ? styles.gatedNoteFeed : styles.gatedNote;

  if (!isOmniAdmin) {
    return <span className={noteClass}>Kein Zugriff auf das Aktivitäts-Protokoll.</span>;
  }
  if (fetching && allEvents.length === 0) {
    return <span className={noteClass}>Lädt …</span>;
  }
  if (error) {
    return <span className={noteClass}>Aktivität derzeit nicht verfügbar.</span>;
  }

  // Per person: day groups.
  if (!isFeed) {
    if (items.length === 0) {
      return (
        <span className={styles.gatedNote}>
          {emptyNote ?? `Keine Aktivität in den letzten ${DAYS} Tagen.`}
        </span>
      );
    }
    return (
      <div className={styles.stack}>
        {groupByDay(items).map((group) => (
          <section key={group.day} className={styles.day}>
            <h3 className={styles.dayLabel}>{group.label}</h3>
            {group.items.map((item) => (
              <ActivityRow key={item.id} item={item} />
            ))}
          </section>
        ))}
        {allEvents.length >= limit && (
          <div className={styles.more}>
            <Button hierarchy="secondary" height="s" onClick={() => setLimit((l) => l + PAGE)}>
              Mehr laden
            </Button>
          </div>
        )}
      </div>
    );
  }

  const rows = cat === "all" ? items : items.filter((i) => categoryOf(i.raw.action) === cat);

  const columns: TableColumns<ActivityItem>[] = [
    {
      id: "when",
      header: "Wann",
      size: 160,
      cell: ({ row }) => <span className={styles.when}>{row.original.when}</span>,
    },
    {
      id: "actor",
      header: "Akteur",
      size: 260,
      cell: ({ row }) => {
        const e = row.original.raw;
        const agent = actorLabel(names, e.actorType, e.actorId, e.metadata);
        return (
          <span className={styles.actor}>
            <Avatar name={agent} size={26} />
            <span className={styles.actorName}>{agent}</span>
          </span>
        );
      },
    },
    {
      id: "action",
      header: "Aktion",
      size: 360,
      cell: ({ row }) => (
        <span className={styles.actionCell} data-status={row.original.ok ? "ok" : "failed"}>
          <span className={`${styles.ic} ${row.original.ok ? styles.icOk : styles.icErr}`}>
            {row.original.ok ? (
              <CheckIcon style={{ width: 14, height: 14, color: "currentColor" }} />
            ) : (
              <CloseIcon style={{ width: 14, height: 14, color: "currentColor" }} />
            )}
          </span>
          <span>
            <span className={styles.action}>{auditActionLabel(row.original.raw.action)}</span>
            {row.original.detail && <span className={styles.detail}>{row.original.detail}</span>}
          </span>
        </span>
      ),
    },
    {
      id: "target",
      header: "Ziel",
      size: 320,
      cell: ({ row }) => (
        <span className={styles.target}>
          {targetLabel(names, row.original.raw.targetId, row.original.raw.metadata)}
        </span>
      ),
    },
    {
      id: "device",
      header: "Gerät",
      size: 220,
      cell: ({ row }) => (
        <span className={styles.target}>
          {[row.original.device, row.original.ip].filter(Boolean).join(" · ") || "—"}
        </span>
      ),
    },
  ];

  return (
    <>
      <div className={styles.chips}>
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            type="button"
            className={styles.chipBtn}
            aria-pressed={cat === c.id}
            onClick={() => setCat(c.id)}
          >
            <Chip value={c.label} active={cat === c.id} isSmall />
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className={styles.empty}>
          <span className={styles.emptyBubble}>
            <HistoryIcon style={{ fontSize: 24 }} />
          </span>
          <div className={styles.emptyTitle}>Keine Ereignisse</div>
          <div className={styles.emptySub}>
            {emptyNote ?? `Keine Aktivität in den letzten ${DAYS} Tagen.`}
          </div>
        </div>
      ) : (
        // The table captures its column renderers on mount; the id→name lists
        // arrive later (thousands of users), so remount once they are in.
        <Table<ActivityItem>
          key={`names-${names.size}`}
          columns={columns}
          data={rows}
          isDesktop
          showShadow
          showBorderRadius
        />
      )}
    </>
  );
}
